import * as React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { ApiDocsContent } from '../components/ApiDocsContent';
import type { ServerExample } from '../components/ApiDocsContent';
import { ACCOUNT_DEVELOPER_PATH } from '@/routes/paths';
import { useAuth } from '@/hooks';
import waveContactListSvg from '@/assets/images/svg/wave_element_contact_list.svg';

function getDocsApiBaseUrl(): string {
  if (typeof window === 'undefined') return '/api';
  return `${window.location.origin}/api`;
}

export const ApiDocsPage: React.FC = () => {
  const { user } = useAuth();
  const canUseDeveloperTools = Boolean(user?.is_developer || user?.role === 'admin');
  const [copied, setCopied] = React.useState<string | null>(null);
  const apiBaseUrl = getDocsApiBaseUrl();

  if (!canUseDeveloperTools) {
    return <Navigate to='/account' replace />;
  }

  const copyToClipboard = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    window.setTimeout(() => setCopied(null), 1800);
  };

  const examples: ServerExample[] = [
    {
      id: 'node',
      label: 'Node.js',
      title: 'Node.js backend route',
      description: 'Express route that your frontend can call without ever seeing the API key.',
      value: `import crypto from "node:crypto";
import express from "express";

const router = express.Router();
const DECHO_BASE_URL = process.env.DECHO_API_BASE_URL ?? "${apiBaseUrl}";
const DECHO_API_KEY = process.env.DECHO_API_KEY;

router.post("/api/echo-chat", async (req, res) => {
  if (!DECHO_API_KEY) {
    return res.status(500).json({ error: "DECHO_API_KEY is not configured" });
  }

  const upstream = await fetch(DECHO_BASE_URL + "/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": DECHO_API_KEY,
      "X-Request-ID": crypto.randomUUID()
    },
    body: JSON.stringify({
      echo_id: req.body.echoId,
      message: req.body.message,
      conversation_id: req.body.conversationId,
      metadata: {
        customer_id: req.body.customerId
      },
      stream: false
    })
  });

  const data = await upstream.json();
  if (!upstream.ok) {
    return res.status(upstream.status).json(data);
  }

  return res.json({
    reply: data.choices[0].message.content,
    conversationId: data.conversation_id,
    requestId: data.request_id
  });
});

export default router;`,
    },
    {
      id: 'python',
      label: 'Python',
      title: 'Python FastAPI route',
      description: 'FastAPI route that forwards a validated backend request to D.ECHO.',
      value: `import os
from uuid import uuid4

import httpx
from fastapi import FastAPI, HTTPException
from pydantic import BaseModel

app = FastAPI()
DECHO_BASE_URL = os.getenv("DECHO_API_BASE_URL", "${apiBaseUrl}")
DECHO_API_KEY = os.environ["DECHO_API_KEY"]


class EchoChatRequest(BaseModel):
    echo_id: str
    message: str
    conversation_id: str | None = None
    customer_id: str | None = None


@app.post("/echo-chat")
async def echo_chat(payload: EchoChatRequest):
    async with httpx.AsyncClient(timeout=30) as client:
        upstream = await client.post(
            f"{DECHO_BASE_URL}/v1/chat/completions",
            headers={
                "Content-Type": "application/json",
                "X-API-Key": DECHO_API_KEY,
                "X-Request-ID": f"req_{uuid4().hex}",
            },
            json={
                "echo_id": payload.echo_id,
                "message": payload.message,
                "conversation_id": payload.conversation_id,
                "metadata": {"customer_id": payload.customer_id},
                "stream": False,
            },
        )

    data = upstream.json()
    if upstream.status_code >= 400:
        raise HTTPException(status_code=upstream.status_code, detail=data)

    return {
        "reply": data["choices"][0]["message"]["content"],
        "conversation_id": data["conversation_id"],
        "request_id": data["request_id"],
    }`,
    },
    {
      id: 'php',
      label: 'PHP',
      title: 'PHP backend endpoint',
      description: 'Plain PHP endpoint using cURL on the server.',
      value: `<?php
$baseUrl = getenv('DECHO_API_BASE_URL') ?: '${apiBaseUrl}';
$apiKey = getenv('DECHO_API_KEY');
$input = json_decode(file_get_contents('php://input'), true);

if (!$apiKey) {
    http_response_code(500);
    echo json_encode(['error' => 'DECHO_API_KEY is not configured']);
    exit;
}

$payload = [
    'echo_id' => $input['echo_id'] ?? '',
    'message' => $input['message'] ?? '',
    'conversation_id' => $input['conversation_id'] ?? null,
    'metadata' => ['customer_id' => $input['customer_id'] ?? null],
    'stream' => false,
];

$ch = curl_init("$baseUrl/v1/chat/completions");
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        'Content-Type: application/json',
        'X-API-Key: ' . $apiKey,
        'X-Request-ID: req_' . bin2hex(random_bytes(16)),
    ],
    CURLOPT_POSTFIELDS => json_encode($payload),
]);

$raw = curl_exec($ch);
$status = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

http_response_code($status);
echo $raw;`,
    },
    {
      id: 'ruby',
      label: 'Ruby',
      title: 'Ruby backend method',
      description: 'Ruby service method using only standard library HTTP tools.',
      value: `require "json"
require "net/http"
require "securerandom"

def chat_with_echo(echo_id:, message:, conversation_id: nil, customer_id: nil)
  base_url = ENV.fetch("DECHO_API_BASE_URL", "${apiBaseUrl}")
  uri = URI("#{base_url}/v1/chat/completions")

  request = Net::HTTP::Post.new(uri)
  request["Content-Type"] = "application/json"
  request["X-API-Key"] = ENV.fetch("DECHO_API_KEY")
  request["X-Request-ID"] = "req_#{SecureRandom.hex(16)}"
  request.body = {
    echo_id: echo_id,
    message: message,
    conversation_id: conversation_id,
    metadata: { customer_id: customer_id },
    stream: false
  }.to_json

  response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: uri.scheme == "https") do |http|
    http.request(request)
  end

  data = JSON.parse(response.body)
  raise data.to_json unless response.is_a?(Net::HTTPSuccess)

  {
    reply: data.fetch("choices").first.fetch("message").fetch("content"),
    conversation_id: data.fetch("conversation_id"),
    request_id: data.fetch("request_id")
  }
end`,
    },
  ];

  return (
    <div className='relative size-full overflow-hidden bg-white'>
      <div className='pointer-events-none absolute inset-0 bg-account-page-gradient' />
      <img
        src={waveContactListSvg}
        alt=''
        className='pointer-events-none absolute bottom-0 left-0 z-0 select-none'
      />

      <div className='custom-scrollbar relative z-10 size-full overflow-y-auto p-6 md:p-10'>
        <PageHeader title='API DOCS' titleClassName='text-brand-800' theme='dark' />

        <div className='mt-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
          <p className='max-w-3xl text-14 text-gray-500'>
            Server-side reference for the API-key Chat API, including text and voice messages. Your
            frontend should call your own backend, and your backend should call D.ECHO with the API
            key.
          </p>
          <Link
            to={ACCOUNT_DEVELOPER_PATH}
            className='shrink-0 text-14 font-bold text-brand-800 underline-offset-2 hover:underline'
          >
            Back to Developer settings
          </Link>
        </div>

        <div className='mt-8'>
          <ApiDocsContent
            apiBaseUrl={apiBaseUrl}
            examples={examples}
            copied={copied}
            onCopy={copyToClipboard}
          />
        </div>
      </div>
    </div>
  );
};
